import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DropdownItem {
  value: any;
  label: string;
  label_en: string;
}

interface Props {
  data?: DropdownItem[] | Record<number, DropdownItem>; // Accepte tableau OU objet
  onSelect: (item: DropdownItem) => void;
  placeholder?: string;
  value?: string;
}
const SearchableDropdown: React.FC<Props> = ({
  data,
  onSelect,
  placeholder = 'Rechercher...',
  value = ''
}) => {
  const [query, setQuery] = useState(value);
  const [show, setShow] = useState(false);
  const { i18n } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Conversion sécurisée en tableau
  const convertToArray = (input: Props['data']): DropdownItem[] => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'object') return Object.values(input);
    return [];
  };

  // Normalisation des données
  const normalizedData = convertToArray(data).map(item => ({
    value: item?.value ?? '',
    label: item?.label ?? '',
    label_en: item?.label_en ?? ''
  }));

  // Filtrage sécurisé
  const filtered = normalizedData.filter(item => {
    const searchText = query.toLowerCase();
    const label = (i18n.language === 'fr' ? item.label : item.label_en) || '';
    return label.toLowerCase().includes(searchText);
  });

  const handleSelect = (item: DropdownItem) => {
    setQuery(item.label);
    setShow(false);
    onSelect(item);
  };

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Gestion du clic externe
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative w-full min-w-[150px] flex-1 max-w-[300px]">
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setShow(true);
          }}
          onFocus={() => setShow(true)}
          className="w-full py-2.5 pl-9 pr-8 text-[15px] bg-[#f9fafb] border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-all duration-200 placeholder:text-slate-400"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setShow(false); }}
            className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
      {show && (
        <div className="absolute bg-white border border-slate-100 w-full max-h-[170px] overflow-y-auto z-30 rounded-xl shadow-xl mt-1.5 py-1 animate-[dropdownIn_.15s_ease-out]">
          <div className="px-3 py-1 text-[11px] uppercase tracking-wider text-slate-400 font-medium">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</div>
          {filtered.length > 0 ? (
            filtered.map((item, i) => (
              <div
                key={`${item.value}-${i}`}
                onClick={() => handleSelect(item)}
                className="px-3 py-2 cursor-pointer text-[15px] text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 hover:pl-4 transition-all duration-150 flex items-center gap-2"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-300 group-hover:text-emerald-500">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
                </svg>
                {i18n.language === 'fr' ? item.label : item.label_en}
              </div>
            ))
          ) : (
            <div className="px-3 py-3 text-slate-500 text-[15px]">
              {i18n.language === 'fr' ? 'Aucun résultat' : 'No results'}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes dropdownIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

export default SearchableDropdown;